import type { CustomCartItemInput } from '@/lib/services/carts/carts.types'
import type {
  Category,
  CategoryIdInput,
  CategoryPosColor,
  UpdateCategoryInput,
} from '@/lib/services/categories/categories.types'
import type { CustomerPosHistoryItem } from '@/lib/services/customers/customers.types'
import { isRecord, normalizeCurrencyValue, type NumericInput, readProperty } from '@/lib/utils'

const categorySlotPrefix = 'pos-category-slot:'
const quickAddPriceStorageKey = 'tukios_flowers:pos_quick_add_category_prices:v1'

export const POS_CATEGORY_COLOR_VALUES: CategoryPosColor[] = [
  'rose',
  'emerald',
  'amber',
  'sky',
  'fuchsia',
  'lime',
]

/** A shortcut grid slot: the id of the category shown there, or null for an empty slot. */
export type CategorySlotId = number | null

/** The organization a remembered quick-add price belongs to. */
type QuickAddOrganizationId = number | string | null | undefined

/** A custom item's starting fields, from a shortcut or a past item. The price is a decimal string. */
export interface PosCustomItemDefaults {
  name: string
  description: string
  notes?: string
  recipe: string
  image: NonNullable<CustomCartItemInput['image']> | null
  price: string
  category_id: number | null
  category_name: string | null
}

export function sortPosCategories<TCategory extends Pick<Category, 'name' | 'pos_sort_order'>>(
  categories: readonly TCategory[] = []
): TCategory[] {
  return [...categories].sort((left, right) => {
    const leftOrder = left.pos_sort_order ?? Number.MAX_SAFE_INTEGER
    const rightOrder = right.pos_sort_order ?? Number.MAX_SAFE_INTEGER

    if (leftOrder !== rightOrder) return leftOrder - rightOrder

    return String(left.name).localeCompare(String(right.name))
  })
}

export function getCategorySlotId(slotIndex: number): string {
  return `${categorySlotPrefix}${slotIndex}`
}

export function getCategorySlotIndexFromId(id: number | string): number | null {
  const value = String(id)

  if (!value.startsWith(categorySlotPrefix)) return null

  const slotIndex = Number(value.slice(categorySlotPrefix.length))

  return Number.isInteger(slotIndex) && slotIndex >= 0 ? slotIndex : null
}

export function getCategorySortOrder(
  category: Pick<Category, 'pos_sort_order'> | null | undefined,
  fallbackOrder: number
): number {
  const order = Number(category?.pos_sort_order)

  return Number.isInteger(order) && order >= 0 ? order : fallbackOrder
}

export function getCategorySlotIds(
  categories: readonly Pick<Category, 'id' | 'name' | 'pos_sort_order'>[] = []
): CategorySlotId[] {
  const sortedCategories = sortPosCategories(categories)
  const slots: CategorySlotId[] = []
  let fallbackOrder = 0

  sortedCategories.forEach((category) => {
    let slotIndex = getCategorySortOrder(category, fallbackOrder)

    while (slots[slotIndex]) slotIndex += 1

    slots[slotIndex] = Number(category.id)
    fallbackOrder = slotIndex + 1
  })

  while (slots.length < sortedCategories.length) slots.push(null)

  return Array.from({ length: slots.length }, (_, index) => slots[index] ?? null)
}

export function addTrailingCategoryDropSlots(
  slotIds: readonly CategorySlotId[] = [],
  trailingSlotCount = 0
): CategorySlotId[] {
  const lastFilledSlotIndex = slotIds.reduce<number>(
    (lastIndex, id, index) => (id ? index : lastIndex),
    -1
  )
  const minimumLength = Math.max(slotIds.length, lastFilledSlotIndex + 1)
  const nextSlotIds = slotIds.slice(0, minimumLength)

  while (nextSlotIds.length < minimumLength + trailingSlotCount) nextSlotIds.push(null)

  return nextSlotIds
}

export function trimTrailingEmptyCategorySlots(
  slotIds: readonly CategorySlotId[] = []
): CategorySlotId[] {
  const lastFilledSlotIndex = slotIds.reduce<number>(
    (lastIndex, id, index) => (id ? index : lastIndex),
    -1
  )

  return lastFilledSlotIndex >= 0 ? slotIds.slice(0, lastFilledSlotIndex + 1) : []
}

export function getFirstAvailableCategorySortOrder(
  categories: readonly Pick<Category, 'show_in_pos' | 'pos_sort_order'>[] = []
): number {
  const usedSortOrders = new Set(
    categories
      .filter((category) => category.show_in_pos)
      .map((category, index) => getCategorySortOrder(category, index))
  )
  let sortOrder = 0

  while (usedSortOrders.has(sortOrder)) sortOrder += 1

  return sortOrder
}

export function isCategorySortOrderOpen(
  categories: readonly Pick<Category, 'id' | 'show_in_pos' | 'pos_sort_order'>[] = [],
  sortOrder: number,
  categoryId: CategoryIdInput | null = null
): boolean {
  return !categories.some((category, index) => {
    if (!category.show_in_pos || String(category.id) === String(categoryId)) return false

    return getCategorySortOrder(category, index) === sortOrder
  })
}

/** Undefined only for a negative sort order, which the shortcut grid never assigns. */
export function getCategoryColorValue(sortOrder = 0): CategoryPosColor | undefined {
  return POS_CATEGORY_COLOR_VALUES[sortOrder % POS_CATEGORY_COLOR_VALUES.length]
}

/** Applies a category's changes to any category cache entry: a list, a page of them, or one. */
export function updateCategoryQueryData(
  data: unknown,
  categoryId: CategoryIdInput,
  updates: UpdateCategoryInput
): unknown {
  if (Array.isArray(data)) {
    const categories: unknown[] = data

    return categories.map((category) =>
      isRecord(category) && String(category.id) === String(categoryId)
        ? { ...category, ...updates }
        : category
    )
  }

  if (!isRecord(data)) {
    return data
  }

  if (Array.isArray(data.data)) {
    return { ...data, data: updateCategoryQueryData(data.data, categoryId, updates) }
  }

  if (isRecord(data.data) && String(data.data.id) === String(categoryId)) {
    return { ...data, data: { ...data.data, ...updates } }
  }

  if (String(data.id) === String(categoryId)) return { ...data, ...updates }

  return data
}

export function getCustomItemDefaultsFromCategory(
  category: Pick<Category, 'id' | 'name'> | null | undefined
): PosCustomItemDefaults {
  return {
    name: category?.name ?? '',
    description: '',
    recipe: '',
    image: null,
    price: '',
    category_id: category?.id ?? null,
    category_name: category?.name ?? null,
  }
}

export function getRememberedQuickAddCategoryPrice({
  orgId,
  categoryId,
}: {
  orgId: QuickAddOrganizationId
  categoryId: CategoryIdInput | null | undefined
}): string {
  if (!categoryId) return ''

  const rememberedPrice = readQuickAddPriceMemory()[getQuickAddPriceKey(orgId, categoryId)]

  return typeof rememberedPrice === 'string' ? rememberedPrice : ''
}

export function rememberQuickAddCategoryPrice({
  orgId,
  categoryId,
  price,
}: {
  orgId: QuickAddOrganizationId
  categoryId: CategoryIdInput | null | undefined
  price: NumericInput
}): void {
  if (!categoryId || typeof globalThis.localStorage === 'undefined') return

  const normalizedPrice = normalizeCurrencyValue(price)

  if (!Number.isFinite(normalizedPrice) || normalizedPrice <= 0) return

  const nextMemory = {
    ...readQuickAddPriceMemory(),
    [getQuickAddPriceKey(orgId, categoryId)]: normalizedPrice.toFixed(2),
  }

  try {
    globalThis.localStorage.setItem(quickAddPriceStorageKey, JSON.stringify(nextMemory))
  } catch {
    // Local memory is a convenience only; failing to persist should not block order entry.
  }
}

export function getCustomItemDefaultsFromHistoryItem(
  item:
    | Pick<
        CustomerPosHistoryItem,
        | 'name'
        | 'description'
        | 'notes'
        | 'recipe'
        | 'image'
        | 'price'
        | 'category_id'
        | 'category_name'
      >
    | null
    | undefined
): PosCustomItemDefaults {
  const imageId = readProperty(item?.image, 'id')
  const image =
    item?.image && typeof imageId === 'number' && imageId ? { ...item.image, id: imageId } : null

  return {
    name: item?.name ?? '',
    description: item?.description ?? '',
    notes: item?.notes ?? '',
    recipe: item?.recipe ?? '',
    image,
    price: item?.price ?? '',
    category_id: item?.category_id ?? null,
    category_name: item?.category_name ?? null,
  }
}

function getQuickAddPriceKey(orgId: QuickAddOrganizationId, categoryId: CategoryIdInput): string {
  return `${orgId ?? 'unknown'}:${categoryId}`
}

function readQuickAddPriceMemory(): Record<string, unknown> {
  if (typeof globalThis.localStorage === 'undefined') return {}

  try {
    const parsed: unknown = JSON.parse(
      globalThis.localStorage.getItem(quickAddPriceStorageKey) ?? '{}'
    )

    return isRecord(parsed) && !Array.isArray(parsed) ? parsed : {}
  } catch {
    return {}
  }
}
